import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";

const size = {
  width: 100,
  height: 100,
};

const COLORS = ["#FF9800", "#8E24AA", "#F06292", "#F50057", "#3384E8"];

// Styled Center Text
const CenterNumber = styled("text")(() => ({
  fill: "#000",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 22,
  fontWeight: 700,
}));

const CenterLabel = styled("text")(() => ({
  fill: "grey",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 10,
  fontWeight: 500,
}));

// Center Label Component
function PieCenterLabel({ total }: { total: number }) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  return (
    <>
      <CenterNumber x={centerX} y={centerY - 10}>
        {total}
      </CenterNumber>
      <CenterLabel width={30} x={centerX} y={centerY + 10}>
        <tspan x={centerX} dy="0">
          Not Ready
        </tspan>
        <tspan x={centerX} dy="10">
          Integration
        </tspan>
      </CenterLabel>
    </>
  );
}

// Custom Legend
function CustomLegend({ data }: { data: any[] }) {
  return (
    <Box display="flex" flexWrap="wrap" width={230} gap="12px" ml={2}>
      {data.map((entry, index) => (
        <Box key={entry.label}>
          <Typography
            fontWeight={600}
            sx={{
              borderBottom: `2px solid ${COLORS[index]}`,
              fontSize: "13px",
              color: "#00000099",
            }}
          >
            {entry.label}
          </Typography>
          <Box display="flex" gap="8px">
            <Typography sx={{ color: COLORS[index], fontWeight: 600 }}>
              {entry.count}
            </Typography>
            <Typography sx={{ color: COLORS[index] }}>
              {entry.percent}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// Main Demo Component
export default function BrandConfigurationReportChart() {
  const loadedCount = 50;
  const total = 100;
  const loadedPerc = (loadedCount / total) * 100;

  const chartData = [
    {
      label: "Properties",
      count: loadedCount,
      percent: `${Math.round(loadedPerc)}%`,
      value: loadedCount,
      color: COLORS[0],
    },
    {
      label: "Unifier",
      count: total - loadedCount,
      percent: `${100 - Math.round(loadedPerc)}%`,
      value: total - loadedCount,
      color: COLORS[1],
    },
    {
      label: "Alt. Identifier",
      count: total - loadedCount,
      percent: `${100 - Math.round(loadedPerc)}%`,
      value: total - loadedCount,
      color: COLORS[2],
    },
    {
      label: "Fetcher",
      count: total - loadedCount,
      percent: `${100 - Math.round(loadedPerc)}%`,
      value: total - loadedCount,
      color: COLORS[3],
    },
    {
      label: "Advisor",
      count: total - loadedCount,
      percent: `${100 - Math.round(loadedPerc)}%`,
      value: total - loadedCount,
      color: COLORS[4],
    },
  ];

  return (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <PieChart
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        series={[
          {
            data: chartData.map((d) => ({
              label: d.label,
              value: d.value,
              color: d.color,
            })),
            innerRadius: 40,
            outerRadius: 50,
          } as any,
        ]}
        {...size}
        slotProps={{ legend: { hidden: true } }}
      >
        <PieCenterLabel total={total} />
      </PieChart>

      <CustomLegend data={chartData} />
    </Stack>
  );
}
